import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RootState } from 'store/ducks';
import { useDispatch, useSelector } from 'react-redux';

import Table from '../../index';
import { CteInvoicingTableSearch } from './CteInvoicingTableSearch';

import { getMaxDateInSearchFilter, getMinDateInSearchFilter } from 'utils/Date';
import { DateOutputStr } from 'utils/DateOutput';

import CteServices from 'services/cte.service';
import { CTeInvoicingTableProps, TableInvoicingCTeFilterState } from './interface';

import { IBatchRequestResponse } from 'models/Batch';
import { CTeInvoiceState } from 'models/CTEs';
import { useLoadCte } from 'hooks/useLoadCte';

export const CTEInvoicingTable: React.FC<CTeInvoicingTableProps> = (props) => {

  const {
    trackingKey,
    tableSearchClassName,
    currentTabId,
    DropdownTemplate,
    isEditing
  } = props;

  const { customerIds } = useLoadCte();

  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const customer = useSelector((state: RootState) => state.customer);

  const cteService = new CteServices();

  const cteInvoiceSelected = useSelector((state: RootState) => state.openProtocolData?.cteInvoice);

  const [tableFilter, setTableFilter] = useState<TableInvoicingCTeFilterState>({
    startPeriod: new Date(`${DateOutputStr(getMinDateInSearchFilter()
      .toISOString(), 'yyyy-MM-dd')}T00:00:00.000Z`),
    lastPeriod: new Date(`${DateOutputStr(getMaxDateInSearchFilter()
      .toISOString(), 'yyyy-MM-dd')}T23:59:59.999Z`),
  });

  const [dataCte, setDataCte] = useState<IBatchRequestResponse<CTeInvoiceState>>();

  const [tableOrderBy, setTableOrderBy] = useState('desc');
  const [currentPage, setCurrentPage] = useState(0);

  const pageTotal = props.pageSize || 10;

  useEffect(() => {
    if (!currentTabId) return;
    dispatch({
      type: 'SET_CURRENT_TAB',
      data: currentTabId
    });
  }, []);

  useEffect(() => {
    if (isEditing && cteInvoiceSelected && cteInvoiceSelected?.length > 0)
      tableFilter.title = cteInvoiceSelected[0].title;
  }, []);

  useEffect(() => {
    getInvoicingCte();
  }, [currentPage, tableFilter, tableOrderBy, customer, user?.selectedCustomerId]);

  const getInvoicingCte = useCallback(() => {
    setDataCte(undefined);
    cteService
      .getInvoiceCTe(currentPage, pageTotal, tableOrderBy, tableFilter, customerIds)
      .then((result: IBatchRequestResponse<CTeInvoiceState>) => {
        if (result) {
          const { error, data, totalPages, pageSize, total } = result;
          setDataCte({ error, data, totalPages, pageSize, total });
        }
      });
  }, [currentPage, tableFilter, tableOrderBy, customer, user?.selectedCustomerId]);

  const CTETableRender = useMemo(() => (
    <Table
      trackingKey={trackingKey}
      className="mt-3"
      error={dataCte?.error || false}
      onReload={getInvoicingCte}
      setTableOrder={setTableOrderBy}
      tableOrder={tableOrderBy}
      DropdownTemplate={DropdownTemplate || undefined}
      currentPage={currentPage}
      data={dataCte?.data}
      pageSize={pageTotal}
      onPageChange={setCurrentPage}
      totalPages={dataCte?.totalPages}
      model={props.model || []}
      totalOrder={dataCte?.total}
    />
  ), [dataCte, user?.selectedCustomerId, customer, tableOrderBy]);

  return (
    <>
      <CteInvoicingTableSearch
        className={tableSearchClassName}
        setTableFilter={setTableFilter}
        setCurrentPage={setCurrentPage}
      />

      {CTETableRender}
    </>
  );
};
